import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import Icon from 'components/AppIcon';
import { useLeague } from 'contexts/LeagueContext';
import { useAuth } from 'contexts/AuthContext';
import { useMessages } from 'contexts/MessagesContext';

// Full nav list (used by desktop top bar)
const NAV_ITEMS = [
  { label: 'Home', path: '/', icon: 'Home', activeIcon: 'Home' },
  { label: 'Record', path: '/game-recording', icon: 'ClipboardList', activeIcon: 'ClipboardList' },
  { label: 'Stats', path: '/stats', icon: 'BarChart2', activeIcon: 'BarChart2' },
  { label: 'History', path: '/history', icon: 'Clock', activeIcon: 'Clock' },
  { label: 'Players', path: '/players', icon: 'Dribbble', activeIcon: 'Dribbble' },
  { label: 'Members', path: '/members', icon: 'Users', activeIcon: 'Users' },
];

// Mobile primary tabs (4 slots + More)
const MOBILE_PRIMARY = [
  { label: 'Home', path: '/', icon: 'Home' },
  { label: 'Record', path: '/game-recording', icon: 'ClipboardList' },
  { label: 'Stats', path: '/stats', icon: 'BarChart2' },
  { label: 'History', path: '/history', icon: 'Clock' },
];

// Items shown in the "More" menu
const MOBILE_MORE_ITEMS = [
  { label: 'Players', path: '/players', icon: 'Dribbble' },
  { label: 'Members', path: '/members', icon: 'Users' },
  { label: 'Messages', path: '/messages', icon: 'MessageSquare' },
];

const NavigationBar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const moreRef = useRef(null);
  const { activeLeague, clearActiveLeague, userRole } = useLeague();
  const { signOut } = useAuth();
  const { unreadCount } = useMessages();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close More menu on route change
  useEffect(() => {
    setMoreOpen(false);
  }, [location?.pathname]);

  // Close More menu when clicking outside
  useEffect(() => {
    if (!moreOpen) return;
    const handleClick = (e) => {
      if (moreRef.current && !moreRef.current.contains(e.target)) {
        setMoreOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('touchstart', handleClick);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('touchstart', handleClick);
    };
  }, [moreOpen]);

  const isActive = (path) => {
    if (path === '/') return location?.pathname === '/';
    return location?.pathname === path || location?.pathname?.startsWith(path + '/');
  };

  const isMoreActive = MOBILE_MORE_ITEMS.some((item) => isActive(item.path));

  const handleNavClick = (path) => navigate(path);

  const handleKeyDown = (e, path) => {
    if (e?.key === 'Enter' || e?.key === ' ') { e?.preventDefault(); navigate(path); }
  };

  const handleSwitchLeague = () => {
    clearActiveLeague();
    navigate('/leagues');
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/login');
    } catch {}
  };

  const ROLE_LABELS = { owner: 'Owner', admin: 'Admin', member: 'Member' };

  return (
    <>
      {/* Desktop Top Navigation Bar */}
      <header className={`nav-bar transition-athletic ${scrolled ? 'shadow-athletic-md' : ''}`} role="banner">
        {/* Logo */}
        <Link to="/" className="nav-logo-container" aria-label="HoopStats - Go to Home">
          <div className="nav-logo" aria-hidden="true">
            <span style={{ fontSize: '18px', lineHeight: 1 }}>🏀</span>
          </div>
          <span className="nav-logo-text hidden sm:block">Hoop<span>Stats</span></span>
        </Link>

        {/* League name badge + role (desktop) */}
        {activeLeague && (
          <div className="hidden lg:flex items-center gap-1.5 ml-2">
            <button
              onClick={handleSwitchLeague}
              className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg font-caption text-xs font-medium transition-all hover:opacity-80"
              style={{ backgroundColor: 'rgba(232,93,4,0.1)', color: 'var(--color-primary)' }}
              title="Switch league"
            >
              <Icon name="Shield" size={12} color="currentColor" />
              {activeLeague.name}
              <Icon name="ChevronDown" size={10} color="currentColor" />
            </button>
            <span
              className="px-1.5 py-0.5 rounded font-caption text-xs font-medium"
              style={{ backgroundColor: 'var(--color-muted)', color: 'var(--color-muted-foreground)' }}
            >
              {ROLE_LABELS[userRole] || 'Member'}
            </span>
          </div>
        )}

        {/* Desktop Navigation Items */}
        <nav className="nav-items hidden lg:flex" role="navigation" aria-label="Primary navigation">
          {NAV_ITEMS?.map((item) => {
            const active = isActive(item?.path);
            return (
              <button
                key={item?.path}
                className={`nav-item ${active ? 'active' : ''}`}
                onClick={() => handleNavClick(item?.path)}
                onKeyDown={(e) => handleKeyDown(e, item?.path)}
                aria-current={active ? 'page' : undefined}
                aria-label={item?.label}
                tabIndex={0}
              >
                <Icon name={active ? item?.activeIcon : item?.icon} size={18} color={active ? 'var(--color-primary)' : 'currentColor'} strokeWidth={active ? 2.5 : 2} />
                <span>{item?.label}</span>
              </button>
            );
          })}
        </nav>

        {/* Right side actions (desktop) */}
        <div className="hidden lg:flex items-center gap-2 ml-4">
          {/* Messages icon with unread badge */}
          <button
            className={`nav-item relative ${isActive('/messages') ? 'active' : ''}`}
            aria-label="Messages"
            onClick={() => navigate('/messages')}
            title="Messages"
          >
            <Icon name="MessageSquare" size={18} color={isActive('/messages') ? 'var(--color-primary)' : 'currentColor'} />
            {unreadCount > 0 && (
              <span
                className="absolute -top-1 -right-1 w-4 h-4 rounded-full flex items-center justify-center font-caption text-xs font-bold text-white"
                style={{ backgroundColor: 'var(--color-primary)', fontSize: '10px' }}
              >
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </button>
          <button
            className="nav-item"
            aria-label="Toggle dark mode"
            onClick={() => document.documentElement?.classList?.toggle('dark')}
          >
            <Icon name="Moon" size={18} color="currentColor" />
          </button>
          <button
            className="nav-item"
            aria-label="Sign out"
            onClick={handleSignOut}
            title="Sign out"
          >
            <Icon name="LogOut" size={18} color="currentColor" />
          </button>
        </div>

        {/* Mobile: League name + page title */}
        <div className="flex lg:hidden flex-1 justify-center items-center gap-2">
          {activeLeague && (
            <button
              onClick={handleSwitchLeague}
              className="flex items-center gap-1 px-2 py-0.5 rounded-md font-caption text-xs"
              style={{ backgroundColor: 'rgba(232,93,4,0.1)', color: 'var(--color-primary)' }}
            >
              <Icon name="Shield" size={10} color="currentColor" />
              {activeLeague.name}
            </button>
          )}
          <span className="font-heading font-700 text-base" style={{ color: 'var(--color-foreground)' }}>
            {NAV_ITEMS?.find((item) => isActive(item?.path))?.label || (isActive('/messages') ? 'Messages' : isActive('/live-game') ? 'Live Game' : 'HoopStats')}
          </span>
        </div>

        {/* Mobile right action */}
        <div className="flex lg:hidden items-center gap-1 ml-auto">
          {/* Messages icon (mobile) */}
          <button
            className="nav-item p-2.5 relative"
            aria-label="Messages"
            onClick={() => navigate('/messages')}
          >
            <Icon name="MessageSquare" size={18} color={isActive('/messages') ? 'var(--color-primary)' : 'currentColor'} />
            {unreadCount > 0 && (
              <span
                className="absolute top-0.5 right-0.5 w-4 h-4 rounded-full flex items-center justify-center font-caption font-bold text-white"
                style={{ backgroundColor: 'var(--color-primary)', fontSize: '9px' }}
              >
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </button>
          <button
            className="nav-item p-2.5"
            aria-label="Toggle dark mode"
            onClick={() => document.documentElement?.classList?.toggle('dark')}
          >
            <Icon name="Moon" size={18} color="currentColor" />
          </button>
          <button
            className="nav-item p-2.5"
            aria-label="Sign out"
            onClick={handleSignOut}
          >
            <Icon name="LogOut" size={18} color="currentColor" />
          </button>
        </div>
      </header>

      {/* Mobile Bottom Tab Bar */}
      <nav className="bottom-tab-bar lg:hidden" role="navigation" aria-label="Mobile navigation">
        {MOBILE_PRIMARY.map((item) => {
          const active = isActive(item?.path);
          return (
            <button
              key={item?.path}
              className={`tab-item ${active ? 'active' : ''}`}
              onClick={() => handleNavClick(item?.path)}
              onKeyDown={(e) => handleKeyDown(e, item?.path)}
              aria-current={active ? 'page' : undefined}
              aria-label={item?.label}
              tabIndex={0}
            >
              <span className="tab-icon-wrap">
                <Icon name={item?.icon} size={20} color={active ? 'var(--color-primary)' : 'currentColor'} strokeWidth={active ? 2.5 : 2} />
              </span>
              <span>{item?.label}</span>
            </button>
          );
        })}

        {/* More tab */}
        <div ref={moreRef} className="relative flex-1 flex">
          <button
            className={`tab-item w-full ${isMoreActive || moreOpen ? 'active' : ''}`}
            onClick={() => setMoreOpen((v) => !v)}
            aria-label="More"
            aria-expanded={moreOpen}
            tabIndex={0}
          >
            <span className="tab-icon-wrap">
              <Icon name="MoreHorizontal" size={20} color={isMoreActive || moreOpen ? 'var(--color-primary)' : 'currentColor'} strokeWidth={isMoreActive || moreOpen ? 2.5 : 2} />
            </span>
            <span>More</span>
          </button>

          {moreOpen && (
            <div
              className="absolute bottom-full right-2 mb-2 rounded-xl border overflow-hidden min-w-[180px]"
              style={{ backgroundColor: 'var(--color-card)', borderColor: 'var(--color-border)', boxShadow: 'var(--shadow-lg)' }}
            >
              {MOBILE_MORE_ITEMS.map((item) => {
                const active = isActive(item.path);
                return (
                  <button
                    key={item.path}
                    className="w-full flex items-center gap-3 px-4 py-3 font-caption text-sm transition-colors hover:opacity-80"
                    style={{
                      color: active ? 'var(--color-primary)' : 'var(--color-foreground)',
                      backgroundColor: active ? 'rgba(232,93,4,0.08)' : 'transparent',
                      borderBottom: '1px solid var(--color-border)',
                    }}
                    onClick={() => {
                      setMoreOpen(false);
                      navigate(item.path);
                    }}
                  >
                    <Icon name={item.icon} size={16} color={active ? 'var(--color-primary)' : 'currentColor'} />
                    <span className="font-medium">{item.label}</span>
                    {item.path === '/messages' && unreadCount > 0 && (
                      <span
                        className="ml-auto w-5 h-5 rounded-full flex items-center justify-center font-caption text-xs font-bold text-white"
                        style={{ backgroundColor: 'var(--color-primary)' }}
                      >
                        {unreadCount > 9 ? '9+' : unreadCount}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </nav>
    </>
  );
};

export default NavigationBar;
